import { useContext, useEffect, useRef, useState } from 'react';
import type { SearchProps, SearchItemProps } from './types';
import { trackEvent, TrackingContext } from '@gjensidige/core-analytics';
import {
  Flex,
  Input,
  Label,
  Link,
  Text,
  Title,
  Segments,
  Spinner,
  Button
} from '@gjensidige/builders-components';
import { ArrowDown, ArrowRight } from '@gjensidige/builders-icons';
import styles from './search.module.less';

type States = 'idle' | 'loading' | SearchItemProps[];
let timeout: ReturnType<typeof setTimeout> | number;
let ajax: XMLHttpRequest; // Using old shcool XMLHttpRequest as this has native abort functionality

export const Search = ({
  action,
  autoFocus = true,
  phrases,
  query,
  searchAPIUrl,
  segments
}: SearchProps) => {
  const { options: trackingOptions } = useContext(TrackingContext);
  const [state, setState] = useState<States>('idle');
  const searchRef = useRef<HTMLInputElement>(null);
  const [resultCountProducts, setResultCountProducts] = useState(0);
  const [resultCountOthers, setResultCountOthers] = useState(0);
  const [currentSegment, setCurrentSegment] = useState('');
  const [excludeArray, setExcludeArray] = useState<string[] | null>(null);

  const ITEMS_TO_SHOW = 10;
  const [itemsToShow, setItemsToShow] = useState(ITEMS_TO_SHOW);

  const handleSearch = () => {
    const value = searchRef.current?.value || '';
    const shouldSearch = value.length >= 2;
    setState(shouldSearch ? 'loading' : 'idle');
    clearTimeout(timeout);

    if (ajax) ajax.abort(); // Cancel previous call
    if (searchAPIUrl && shouldSearch) {
      timeout = setTimeout(() => {
        if (!ajax) ajax = new XMLHttpRequest();
        ajax.onload = () => onResults(JSON.parse(ajax.responseText));
        ajax.open('POST', searchAPIUrl, true);
        ajax.setRequestHeader('Content-Type', 'application/json');
        ajax.send(
          JSON.stringify({
            query: value,
            filter: currentSegment === '/' ? '' : currentSegment,
            excludeFilter: currentSegment === '/' ? excludeArray : []
          })
        );
      }, 2000);
    }
  };

  const onResults = (data: SearchItemProps[]) => {
    setState(data);
    trackEvent(
      {
        eventAction: 'search',
        eventCategory: 'site-search',
        eventKey: 'input',
        eventLabel: searchRef.current?.value || '',
        eventStatus: data?.length ? 'success' : 'error'
      },
      trackingOptions
    );
  };

  useEffect(() => {
    setTimeout(() => {
      const input = searchRef.current;
      const event = new Event('input', { bubbles: true }); // Allow react to render before setting focus
      if (autoFocus) input?.focus();
      if (!ajax) input?.dispatchEvent(event); // Trigger search on first render
    }, 300);
  }, [autoFocus, state, query]);

  const products =
    Array.isArray(state) && state.length
      ? state.filter(({ product }) => product)
      : [];
  const COUNT_ALL = state.length;
  const COUNT_PRODUCTS = products.length;
  setResultCountProducts(COUNT_PRODUCTS);
  const COUNT_OTHER = COUNT_ALL - COUNT_PRODUCTS;
  setResultCountOthers(COUNT_OTHER);

  const listOfSegments: SearchProps['segments'] = [
    {
      text: phrases?.labels?.segmentOptionAll,
      url: ''
    }
  ];

  if (segments) listOfSegments.push(...segments);

  const getSegmentUrls = (): string[] => {
    const segmentUrls: string[] = [];
    listOfSegments?.forEach((segment) => {
      if (segment.url && segment.url !== '/') {
        segmentUrls.push(segment.url.slice(1));
      }
    });

    return segmentUrls;
  };

  useEffect(() => {
    if (listOfSegments) {
      setExcludeArray(getSegmentUrls());
    }
  }, []);

  const segmentClick = (segmentUrl: string | undefined) => {
    setCurrentSegment(segmentUrl || '');
    setItemsToShow(ITEMS_TO_SHOW); // reset when changing segment

    if (segmentUrl === undefined) return;
    if (ajax) ajax.abort(); // Cancel previous call
    if (searchAPIUrl && searchRef.current?.value) {
      if (!ajax) ajax = new XMLHttpRequest();
      ajax.onload = () => onResults(JSON.parse(ajax.responseText));
      ajax.open('POST', searchAPIUrl, true);
      ajax.setRequestHeader('Content-Type', 'application/json');
      ajax.send(
        JSON.stringify({
          query: searchRef.current?.value || '',
          filter: segmentUrl === '/' ? '' : segmentUrl,
          excludeFilter: segmentUrl === '/' ? excludeArray : [] // if segmentUrl is '/' we want to exclude the other segments to avoid duplicates
        })
      );
    }
  };

  const showLoadMoreButton = Array.isArray(state) && itemsToShow < state.length;

  return (
    <Flex layout="1" gap="md" className={styles.search}>
      <form
        className={styles.searchForm}
        method="get"
        action={action?.href}
        onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
      >
        <Label>
          {phrases?.labels?.input || 'Search'}
          <Input
            autoComplete="off"
            autoFocus={autoFocus}
            classNameWrapper={styles.input}
            defaultValue={query}
            name="q"
            onChange={handleSearch}
            ref={searchRef}
            required
            tracking={false}
            type="search"
          />
        </Label>
      </form>

      <div className={styles.resultsBlock}>
        {!!listOfSegments && Array.isArray(state) && (
          <div className={styles.blockSegmentFilter}>
            <Label>{phrases?.labels?.segment}</Label>
            <Segments defaultValue={0}>
              <Segments.Tablist className={styles.tablist}>
                {listOfSegments.map((segment, index) => (
                  <Segments.Tab
                    key={index}
                    onClick={() => segmentClick(segment.url)}
                    className={styles.segmentsTab}
                  >
                    {segment?.text}
                  </Segments.Tab>
                ))}
              </Segments.Tablist>
              {listOfSegments.map((_segment, index) => (
                <Segments.Panel key={index}>
                  {!!(Array.isArray(state) && state.length) && (
                    <>
                      {COUNT_PRODUCTS > 0 && (
                        <div className={styles.resultsProducts}>
                          <Title aria-live="polite" as="h2" size="5">
                            <strong>
                              {phrases?.labels?.products || 'Products'}
                            </strong>{' '}
                            <span>({resultCountProducts})</span>
                          </Title>
                          <Flex
                            className={styles.products}
                            gap={{ xs: 'sm xs', md: 'sm' }}
                          >
                            {products.map(
                              ({ product, link }, index) =>
                                product && (
                                  <Link key={index} href={link.url}>
                                    {product}
                                  </Link>
                                )
                            )}
                          </Flex>
                        </div>
                      )}
                      <div className={styles.resultsOther}>
                        <Title aria-live="polite" as="h2" size="5">
                          <strong>
                            {phrases?.labels?.otherResults || 'Other results'}
                          </strong>{' '}
                          <span>({resultCountOthers})</span>
                        </Title>

                        {state
                          .filter(({ product }) => !product)
                          .slice(0, itemsToShow)
                          .map(
                            (
                              { title, preface, link }: SearchItemProps,
                              index
                            ) => (
                              <Flex
                                as="a"
                                key={index}
                                href={link.url}
                                className={styles.item}
                                gap="xs"
                                layout="1"
                              >
                                <Title as="h3" size="6" text={title} />
                                {!!preface && (
                                  <Text size="small" className={styles.preface}>
                                    {preface}
                                  </Text>
                                )}
                                <Flex
                                  aria-hidden="true"
                                  as={Text}
                                  gap="xs"
                                  size="small"
                                  align="center"
                                  justify="start"
                                  className={styles.breadcrumb}
                                >
                                  {breadCrumbGenerator(link.url)}
                                </Flex>
                              </Flex>
                            )
                          )}
                      </div>
                    </>
                  )}
                </Segments.Panel>
              ))}
            </Segments>
          </div>
        )}

        {state === 'loading' && (
          <Flex layout={{ xs: 1, md: 2 }}>
            <Flex gap="xs" layout="1" className={styles.loaders}>
              <Spinner variant="skeleton" size="1.5rem" />
              <Spinner variant="skeleton" size="1.5rem" />
            </Flex>
          </Flex>
        )}

        {!state.length && (
          <div>
            <strong aria-live="polite">
              {phrases?.emptyResult?.title || 'No results'}
            </strong>
            <Text
              as="div"
              className={styles.htmlArea}
              dangerouslySetInnerHTML={{
                __html: phrases?.emptyResult?.text || ''
              }}
              style={{ flex: '1 0 100%' }}
            />
          </div>
        )}
        {state === 'idle' && (
          <div>
            <strong aria-live="polite">
              {phrases?.information?.title || ''}
            </strong>
            <p>{phrases?.information?.text || ''}</p>
          </div>
        )}
      </div>

      {showLoadMoreButton && (
        <div>
          <Button
            variant="action"
            icon={<ArrowDown />}
            iconPosition="right"
            onClick={() => setItemsToShow(itemsToShow + ITEMS_TO_SHOW)}
          >
            {phrases?.moreResults || 'Load more results'}
          </Button>
        </div>
      )}
    </Flex>
  );
};

const breadCrumbGenerator = (target: string) => {
  const breadCrumbsArray = target
    .slice(1)
    .replace(/-/g, ' ')
    .split('/')
    .map((title) => title.slice(0, 1).toUpperCase() + title.slice(1));

  return breadCrumbsArray.map((crumb: string, index: number) => (
    <>
      {!!index && <ArrowRight />}
      <span key={index}>{crumb}</span>
    </>
  ));
};
